/* eslint-disable valid-jsdoc */
import { timeout as rxTimeout } from 'rxjs/operators';
import rxAjaxFetcher from './api-fetcher/fetchers/rx-ajax-fetcher';
import { buildFullPath } from './api-fetcher/utils';
import { v4 as uuidv4 } from 'uuid';
import { getUserEnvironment } from './detect-utils';
import { getKeyFromValue, SocketTypeEnums, TransportEnum } from './enums';
import { formatDateISO8601 } from './moment-utils';
import { getStringByteSize, truncateStringField } from './string-utils';
import { createDevWarn } from './dev-utils';
import { redactHeaders, redactBody } from './log-redact-utils';

// 每個瀏覽器 session 都產生一個唯一的 uuid
export const sessionId = uuidv4();

const LOG_URL = 'elasticsearch';
const FLUSH_DELAY_MS = 1000;
// 單批 256KB:對網路差的客戶降低失敗率與單筆耗時(1MB 在 3G 上需 5~10s)。
const MAX_BATCH_BYTES = 256 * 1024;
// 每批 request 10 秒上限,避免不穩網路下連線無限 hang 住佔 connection pool。
const LOG_REQUEST_TIMEOUT_MS = 10 * 1000;
// Pending batches 上限:離線/持續 timeout 情境下避免記憶體無上限堆積。
// 超過時丟掉最舊的批次。5 × 256KB ≈ 1.25MB 上限。
const MAX_PENDING_BATCHES = 5;
// 單一批次最多嘗試 3 次(1 次首送 + 2 次重試)。4xx 直接放棄,其餘錯誤(network /
// timeout / 5xx)重試。最壞 in-flight 時間:
//   3 attempts × LOG_REQUEST_TIMEOUT_MS + 2 × RETRY_DELAY_MS = 32s
// 期間 _inFlightByBaseUrl=true 鎖住 drain,後續 pending 等待;若 pending 數量
// 撐到 MAX_PENDING_BATCHES 上限,最舊批次會被丟棄(換取記憶體上限)。
const MAX_SEND_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
// Navigator.sendBeacon 各瀏覽器上限約 64KB,unload 時批次以較小 cap 切批
// 以提高 delivery 成功率。
const BEACON_MAX_BYTES = 60 * 1024;
const UNSERIALIZABLE_PLACEHOLDER = '[Unserializable]';
// WS log 的 normalize(redact + stringify)延後到主執行緒 idle 時執行,
// timeout 為保底:頁面持續忙碌時最晚於此時限內執行,避免 log 無限期延後。
const IDLE_LOG_TIMEOUT_MS = 2000;
// Idle 任務數上限:event storm 時淘汰最舊的,與 MAX_PENDING_BATCHES 同哲學
const MAX_PENDING_IDLE_LOGS = 500;
const _devWarn = createDevWarn('[logger]');
const { HTTP, WEB_SOCKET, TRIGGER } = TransportEnum;
const {
	PING,
	PONG,
	CONVERSATIONS_PENDING_ACK_GOT,
	CONVERSATIONS_ACTIVE_ACK_GOT,
	CONVERSATIONS_CLOSED_ACK_GOT,
	CONVERSATIONS_UNREAD_ACK_GOT,
	CONVERSATIONS_REPLY_MONITOR_INTERCEPTED_ACK_GOT,
	CONVERSATION_REPLIES_ACK_GOT,
} = SocketTypeEnums;
// 內容大、筆數多的 list 型 ack 只在事件當下記摘要(messageId / 筆數 / 估算
// 大小),不記完整內容:深層 redact 與整包 JSON.stringify 會產生 long task。
const SUMMARY_WS_EVENT_SET = new Set([
	CONVERSATIONS_PENDING_ACK_GOT,
	CONVERSATIONS_ACTIVE_ACK_GOT,
	CONVERSATIONS_CLOSED_ACK_GOT,
	CONVERSATIONS_UNREAD_ACK_GOT,
	CONVERSATIONS_REPLY_MONITOR_INTERCEPTED_ACK_GOT,
	CONVERSATION_REPLIES_ACK_GOT,
]);
const userEnv = getUserEnvironment();
const _queuesByBaseUrl = new Map();
const _timersByBaseUrl = new Map();
const _drainTimersByBaseUrl = new Map();
const _bytesByBaseUrl = new Map();
const _pendingBatchesByBaseUrl = new Map();
const _inFlightByBaseUrl = new Map();
// 正在送的 batch 本身(尚未 settle):_drainNext 寫入、finish() 清除。
// pagehide 時納入 beacon 一併沖出,避免分頁關閉時 xhr 被瀏覽器中斷而遺失。
const _inFlightBatchByBaseUrl = new Map();
// LastSentAt 紀錄「上一批 settle(success / error / timeout 任一)的時間」,
// 由 `_sendBatch` 的 finish() 寫入。`_scheduleDrain` 以此計算與下一批之間的最小間隔。
const _lastSettledAtByBaseUrl = new Map();
// 將任意值安全轉成字串。失敗時回傳固定 placeholder，避免 stringify throw 中斷上層流程。
const _toStringField = value => {
	if (value === null || value === undefined) {
		return null;
	}

	if (typeof value === 'string') {
		return truncateStringField(value);
	}

	if (value instanceof Error) {
		try {
			return truncateStringField(JSON.stringify({
				name: value.name,
				message: value.message,
				stack: value.stack,
			}));
		} catch (_) {
			return UNSERIALIZABLE_PLACEHOLDER;
		}
	}

	try {
		return truncateStringField(JSON.stringify(value));
	} catch (_) {
		return UNSERIALIZABLE_PLACEHOLDER;
	}
};
// 統一 schema：每個 log item 都帶相同 keys，不適用的欄位為 null。
// 避免 Elasticsearch index mapping 在 HTTP/WS/TRIGGER 混合 payload 下發生型別衝突。
const _normalizeItem = raw => ({
	sessionId,
	transport: raw.transport || null,
	event: raw.event || null,
	category: raw.category || null,
	url: raw.url || null,
	method: raw.method || null,
	headers: _toStringField(redactHeaders(raw.headers)),
	requestBody: _toStringField(redactBody(raw.requestBody)),
	// ResponseData 也走 redaction:login response 內常有 JWT / session token。
	responseData: _toStringField(redactBody(raw.responseData)),
	error: _toStringField(raw.error),
	wsData: _toStringField(redactBody(raw.wsData)),
	extraValue: _toStringField(raw.extraValue),
	// OccurredAt 為事件實際發生時間(ms):延後 normalize 的路徑用它保留
	// 真實 timestamp,否則以進入 queue 的當下為準。
	createTime: formatDateISO8601(raw.occurredAt || Date.now()),
	...userEnv,
});
const _byteSizeOf = item => {
	try {
		return getStringByteSize(JSON.stringify(item));
	} catch (_) {
		return 0;
	}
};
const _splitBatchesByBytes = (items, maxBytes) => {
	const batches = [];
	let current = [];
	let currentBytes = 0;

	items.forEach(item => {
		const itemBytes = _byteSizeOf(item);

		if (current.length > 0 && currentBytes + itemBytes > maxBytes) {
			batches.push(current);
			current = [];
			currentBytes = 0;
		}

		current.push(item);
		currentBytes += itemBytes;
	});

	if (current.length > 0) {
		batches.push(current);
	}

	return batches;
};
const _isRetryableError = err => {
	const status = err?.status;

	// 4xx 視為 client / payload 問題(例如 mapping conflict 仍有可能落到這),
	// 重試也不會成功,直接放棄;其餘(network / timeout / 5xx)都重試。
	if (typeof status === 'number' && status >= 400 && status < 500) {
		return false;
	}

	return true;
};
const _onBatchDone = baseUrl => {
	_inFlightByBaseUrl.set(baseUrl, false);

	_scheduleDrain(baseUrl);
};
const _sendBatch = (batch, baseUrl) => {
	let body;

	try {
		body = JSON.stringify({ data: batch });
	} catch (_) {
		_onBatchDone(baseUrl);
		return;
	}

	let settled = false;
	const finish = () => {
		if (settled) {
			return;
		}

		settled = true;
		// 寫入時間戳語義為「上一批落地時間」,故含失敗 / timeout / 重試耗盡。
		_lastSettledAtByBaseUrl.set(baseUrl, Date.now());
		_inFlightBatchByBaseUrl.delete(baseUrl);
		_onBatchDone(baseUrl);
	};
	const attemptSend = attempt => {
		try {
			// 刻意直接用 rxAjaxFetcher,不走 generateApiFetcher — 後者會在
			// REQUEST/RESPONSE/ERROR 時呼叫 logApiEvent,構成 logger → fetcher
			// → logger 循環。Timeout 與 error swallow 在這裡自行處理。
			rxAjaxFetcher({
				url: LOG_URL,
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body,
				baseUrl,
			}).pipe(rxTimeout(LOG_REQUEST_TIMEOUT_MS)).subscribe({
				next: () => finish(),
				error: err => {
					if (_isRetryableError(err) && attempt < MAX_SEND_ATTEMPTS) {
						setTimeout(() => {
							// 三層 guard,避免殘留 retry callback 干擾後續流程:
							//   - settled:finish() 已執行(同 closure race)
							//   - in-flight batch identity 比對:用 batch 物件
							//     reference 確認 _inFlightBatchByBaseUrl 仍是
							//     「這一批」,涵蓋兩個 race:
							//     (1) pagehide 已透過 beacon 補送此 batch 並
							//         delete 對應 Map entry
							//     (2) bfcache restore 後 _drainNext 已取出
							//         新 batch 接管 in-flight,舊 callback 不應
							//         為已被 beacon 處理掉的舊 batch 再 retry
							if (settled || _inFlightBatchByBaseUrl.get(baseUrl) !== batch) {
								return;
							}

							attemptSend(attempt + 1);
						}, RETRY_DELAY_MS);
						return;
					}

					finish();
				},
			});
		} catch (_) {
			// Rxjs ajax 同步 throw 罕見,不再 retry,直接結束
			finish();
		}
	};

	attemptSend(1);
};
const _drainNext = baseUrl => {
	if (_inFlightByBaseUrl.get(baseUrl)) {
		return;
	}

	const scheduled = _drainTimersByBaseUrl.get(baseUrl);

	if (scheduled) {
		clearTimeout(scheduled);
		_drainTimersByBaseUrl.delete(baseUrl);
	}

	const pending = _pendingBatchesByBaseUrl.get(baseUrl) || [];

	if (pending.length === 0) {
		return;
	}

	const [next, ...rest] = pending;

	_pendingBatchesByBaseUrl.set(baseUrl, rest);
	_inFlightByBaseUrl.set(baseUrl, true);
	_inFlightBatchByBaseUrl.set(baseUrl, next);
	_sendBatch(next, baseUrl);
};
const _calcDrainDelay = baseUrl => {
	const lastSettledAt = _lastSettledAtByBaseUrl.get(baseUrl) || 0;
	const elapsed = Date.now() - lastSettledAt;

	return Math.max(0, FLUSH_DELAY_MS - elapsed);
};
const _scheduleDrain = baseUrl => {
	if (_inFlightByBaseUrl.get(baseUrl) || _drainTimersByBaseUrl.get(baseUrl)) {
		return;
	}

	const pending = _pendingBatchesByBaseUrl.get(baseUrl) || [];

	if (pending.length === 0) {
		return;
	}

	const delay = _calcDrainDelay(baseUrl);

	if (delay === 0) {
		_drainNext(baseUrl);
		return;
	}

	const timer = setTimeout(() => _drainNext(baseUrl), delay);

	_drainTimersByBaseUrl.set(baseUrl, timer);
};
const _flush = baseUrl => {
	const existing = _timersByBaseUrl.get(baseUrl);

	if (existing) {
		clearTimeout(existing);
	}

	_timersByBaseUrl.delete(baseUrl);

	const items = _queuesByBaseUrl.get(baseUrl);

	if (!items || items.length === 0) {
		_bytesByBaseUrl.set(baseUrl, 0);
		return;
	}

	_queuesByBaseUrl.set(baseUrl, []);
	_bytesByBaseUrl.set(baseUrl, 0);

	const newBatches = _splitBatchesByBytes(items, MAX_BATCH_BYTES);
	const pending = _pendingBatchesByBaseUrl.get(baseUrl) || [];
	const merged = [...pending, ...newBatches];
	// 離線時 pending 會持續累積,超過上限丟掉最舊的避免記憶體無上限。
	const capped = merged.length > MAX_PENDING_BATCHES ?
		merged.slice(-MAX_PENDING_BATCHES) :
		merged;

	_pendingBatchesByBaseUrl.set(baseUrl, capped);
	_scheduleDrain(baseUrl);
};
const _enqueue = (rawItem, baseUrl) => {
	if (!baseUrl) {
		return;
	}

	let item;

	try {
		item = _normalizeItem(rawItem);
	} catch (_) {
		return;
	}

	const queue = _queuesByBaseUrl.get(baseUrl) || [];

	// 此 queue 為 module-private,無外部 reference;在 log storm 場景下用 push
	// 避免每筆 log 都複製整個 queue 造成 O(N²) allocation/main-thread 壓力。
	queue.push(item);
	_queuesByBaseUrl.set(baseUrl, queue);

	const accumulatedBytes = (_bytesByBaseUrl.get(baseUrl) || 0) + _byteSizeOf(item);

	_bytesByBaseUrl.set(baseUrl, accumulatedBytes);

	// 護欄:queue 累積超過單批上限就立刻 flush,避免單秒內爆量 log 把記憶體
	// 撐爆/延後送出時 payload 過大。
	if (accumulatedBytes >= MAX_BATCH_BYTES) {
		_flush(baseUrl);
		return;
	}

	// Fixed 1-second window:queue 從空 → 有第一筆 log 時啟動 timer,後續加入
	// 的 log 不再 reset timer,1 秒到一定送出(避免持續高頻 log 時 debounce
	// 永不觸發、starvation)。Timer 在 _flush 內被清空,下一波第一筆會重啟。
	if (_timersByBaseUrl.get(baseUrl)) {
		return;
	}

	const timer = setTimeout(() => _flush(baseUrl), FLUSH_DELAY_MS);

	_timersByBaseUrl.set(baseUrl, timer);
};
// 排程到 idle 的 log 任務集合:集合成員資格即 pending 狀態 —— idle callback、
// pagehide drain、上限淘汰任一先移除後,其餘路徑即不再執行。
const _pendingIdleLogSet = new Set();
const _scheduleIdleLog = task => {
	const run = () => {
		if (!_pendingIdleLogSet.delete(run)) {
			return;
		}

		task();
	};

	if (_pendingIdleLogSet.size >= MAX_PENDING_IDLE_LOGS) {
		const oldest = _pendingIdleLogSet.values().next().value;

		_pendingIdleLogSet.delete(oldest);
	}

	_pendingIdleLogSet.add(run);

	if (typeof requestIdleCallback === 'function') {
		requestIdleCallback(run, { timeout: IDLE_LOG_TIMEOUT_MS });
	} else {
		setTimeout(run, 0);
	}
};
const _drainPendingIdleLogs = () => {
	[..._pendingIdleLogSet].forEach(run => run());
};
// 取 data.data 物件中第一個 array value(summary ack 一律 emit 物件形狀)
const _findPayloadList = data => {
	const inner = data?.data;

	if (inner && typeof inner === 'object') {
		return Object.values(inner).find(Array.isArray) || null;
	}

	return null;
};
// 以首筆 byte size × 筆數估算,避免整包 JSON.stringify 的 O(payload) 工作
const _estimatePayloadBytes = list => {
	if (!list || list.length === 0) {
		return 0;
	}

	try {
		return getStringByteSize(JSON.stringify(list[0])) * list.length;
	} catch (_) {
		return null;
	}
};
const _buildSummaryWsData = ({ event, data }) => {
	const list = _findPayloadList(data);

	return {
		event,
		summary: true,
		messageId: data?.messageId || null,
		count: list ? list.length : null,
		approxBytes: _estimatePayloadBytes(list),
	};
};
const _sendBeacon = (batch, baseUrl) => {
	if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
		return;
	}

	let body;

	try {
		body = JSON.stringify({ data: batch });
	} catch (_) {
		return;
	}

	try {
		// 與一般批次送出對齊使用 application/json — 後端只有 bodyParser.json()。
		// TODO(cross-origin): 跨 origin 部署時 application/json 會觸發 CORS
		// preflight,pagehide 即將關閉頁面、preflight 來不及完成 → beacon 可能
		// 被瀏覽器 cancel,造成最後一秒 log 遺失。同 origin 部署無此問題。
		// 修法:後端安裝 bodyParser.text + normalize middleware 將 string body
		// parse 成 JSON 後,前端 Blob type 改 text/plain 即可走 CORS-safelisted
		// simple request 跳過 preflight。
		const blob = new Blob([body], { type: 'application/json' });
		const fullUrl = buildFullPath(baseUrl, LOG_URL);
		const queued = navigator.sendBeacon(fullUrl, blob);

		if (!queued) {
			// 瀏覽器拒絕 enqueue(常見原因:payload 超過 sendBeacon size cap、
			// 全域 beacon quota 已滿)。Dev 環境提示,production 不噪音。
			_devWarn('sendBeacon rejected', { size: body.length, baseUrl });
		}
	} catch (err) {
		// SendBeacon 為 unload 路徑 best-effort,失敗忽略;dev 環境提示便於排查
		_devWarn('sendBeacon threw', err);
	}
};
const _clearTimersFor = baseUrl => {
	const flushTimer = _timersByBaseUrl.get(baseUrl);

	if (flushTimer) {
		clearTimeout(flushTimer);
		_timersByBaseUrl.delete(baseUrl);
	}

	const drainTimer = _drainTimersByBaseUrl.get(baseUrl);

	if (drainTimer) {
		clearTimeout(drainTimer);
		_drainTimersByBaseUrl.delete(baseUrl);
	}
};
// Idempotent:每次執行都先清 state 再 beacon,呼叫零次 / 多次行為一致。
// 故可長期掛在 pagehide 上,bfcache restore 後仍能在下一次離開頁面 flush。
const _flushAllOnPageHide = () => {
	// 先把尚未執行的 idle log 任務同步跑完進 queue,納入本次 beacon 一併沖出
	_drainPendingIdleLogs();

	const allBaseUrls = new Set();

	_queuesByBaseUrl.forEach((_value, baseUrl) => allBaseUrls.add(baseUrl));
	_pendingBatchesByBaseUrl.forEach((_value, baseUrl) => allBaseUrls.add(baseUrl));
	_inFlightBatchByBaseUrl.forEach((_value, baseUrl) => allBaseUrls.add(baseUrl));

	allBaseUrls.forEach(baseUrl => {
		const queueItems = _queuesByBaseUrl.get(baseUrl) || [];
		const pending = _pendingBatchesByBaseUrl.get(baseUrl) || [];
		// 正在送的 batch 在 pagehide 時 xhr 多半會被瀏覽器中斷,改透過 beacon
		// 補送一份;偶爾與已成功送達的 xhr 重複,優於遺失。
		// 此設計假設後端 / ES 可容忍重複文件;依賴 log 精確計數的 dashboard
		// 需在後端用 `sessionId + createTime` 做 dedup。
		const inFlightBatch = _inFlightBatchByBaseUrl.get(baseUrl) || [];
		// Pending 是 batches array (array<array<item>>),flatten 後與 queue 合併
		const allItems = inFlightBatch.concat(pending.flat()).concat(queueItems);

		// 不論有無 items 都先清 timer + state,避免 bfcache restore 後舊 timer
		// 與新 _enqueue 啟動的 timer 競爭。
		_clearTimersFor(baseUrl);
		_queuesByBaseUrl.set(baseUrl, []);
		_bytesByBaseUrl.set(baseUrl, 0);
		_pendingBatchesByBaseUrl.set(baseUrl, []);
		_inFlightBatchByBaseUrl.delete(baseUrl);
		_inFlightByBaseUrl.set(baseUrl, false);

		if (allItems.length === 0) {
			return;
		}

		// 用較小的 cap (60KB) 切批,讓 sendBeacon 比較不會被瀏覽器以 size limit 拒收
		const beaconBatches = _splitBatchesByBytes(allItems, BEACON_MAX_BYTES);

		beaconBatches.forEach(batch => _sendBeacon(batch, baseUrl));
	});
};

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
	// Pagehide 在 mobile / desktop 一致觸發(unload 在 mobile safari 不可靠),
	// 用 sendBeacon 把 queue + pending + in-flight 沖出,避免關閉分頁時遺失
	// 最後一秒內的 log。listener 故意不移除:bfcache restore 後使用者繼續
	// 操作累積新 log,下一次離開頁面仍須 flush(handler 本身 idempotent)。
	window.addEventListener('pagehide', _flushAllOnPageHide);
}

/**
 * 統一的 log function 供 API 呼叫時使用
 * @param {String} eventType - 前端自定義的 actionType
 * @param {Object} config - API 請求的設定 (例如 url, method, headers, body)
 * @param {Object} data - 附加資料 (如 response 或 error)
 */
export function logApiEvent(eventType, config, data = {}) {
	try {
		_enqueue({
			event: eventType,
			url: config?.url,
			method: config?.method,
			headers: config?.headers,
			requestBody: config?.body,
			responseData: data?.response,
			error: data?.error,
			transport: HTTP,
		}, config?.loggerConfig?.baseUrl);
	} catch (err) {
		// Logger 自身故障不可弄掛 caller,僅在 dev 環境提示便於排查
		_devWarn('public log fn threw', err);
	}
}

/**
 * 用於記錄 websocket 相關訊息
 * @param {String} eventType - 前端自定義的 actionType
 * @param {Object} wsData - websocket 傳送或接收的資料
 * @param {Object} loggerConfig - 包含 enable, baseUrl 等 logger 設定
 */
export function logWebSocketEvent(eventType, wsData, loggerConfig) {
	try {
		const rawEvent = wsData?.event;
		const socketEventKey = getKeyFromValue(SocketTypeEnums, rawEvent) || rawEvent;

		if (socketEventKey === PING || socketEventKey === PONG) {
			return;
		}

		const occurredAt = Date.now();
		// Summary 事件在當下同步取 O(1) 摘要,不保留 payload reference;其餘事件
		// 延後到 idle 才 normalize(redact + stringify),不阻塞同 tick 的 socket
		// handler / emit。契約:延後的 wsData 以 reference 序列化,handler /
		// reducer 不得就地改寫 socket payload,否則 log 會偏離 wire 上的實際值。
		const deferredWsData = SUMMARY_WS_EVENT_SET.has(rawEvent) ? _buildSummaryWsData(wsData) : wsData;

		_scheduleIdleLog(() => {
			try {
				_enqueue({
					event: `${eventType}__${socketEventKey}`,
					wsData: deferredWsData,
					transport: WEB_SOCKET,
					occurredAt,
				}, loggerConfig?.baseUrl);
			} catch (err) {
				_devWarn('deferred ws log threw', err);
			}
		});
	} catch (err) {
		// Logger 自身故障不可弄掛 caller,僅在 dev 環境提示便於排查
		_devWarn('public log fn threw', err);
	}
}

/**
 * 用於記錄 event 相關訊息
 * @param {Object} eventData - event, category, extraValue
 * @param {Object} loggerConfig - 包含 enable, baseUrl 等 logger 設定
 */
export function logTriggerEvent(eventData, loggerConfig) {
	try {
		_enqueue({
			category: eventData?.category,
			event: eventData?.event,
			extraValue: eventData?.extraValue,
			transport: TRIGGER,
		}, loggerConfig?.baseUrl);
	} catch (err) {
		// Logger 自身故障不可弄掛 caller,僅在 dev 環境提示便於排查
		_devWarn('public log fn threw', err);
	}
}
